import React, { useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Dynamically load TidyCal script
    const script = document.createElement('script');
    script.src = "https://asset-tidycal.b-cdn.net/js/embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount if necessary
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  if (!location.state || !location.state.resultType) {
    return <Navigate to="/" replace />;
  }

  const { resultType, answers, formData } = location.state;

  const getResultContent = () => {
    switch (resultType) {
      case 'underinsured':
        return {
          theme: 'danger',
          icon: 'FiAlertTriangle',
          color: 'text-[#EF4444]',
          bg: 'bg-red-50',
          border: 'border-red-200',
          headline: "Your Home May Be Seriously Underinsured",
          intro: "Based on your answers, there's a strong chance your home insurance coverage hasn't kept up with rising construction costs and property improvements.",
          sections: [
            {
              title: "🚨 Critical Gaps Identified:",
              items: [
                "Your replacement cost estimate is likely outdated (construction costs up 40% since 2020)",
                answers.additionalStructures?.length > 1 ? "Your additional property structures may not be fully covered" : null,
                answers.disasterCoverage?.some(r => ['earthquake', 'flood', 'wildfire'].includes(r)) ? "You're missing critical disaster coverage for California" : null
              ].filter(Boolean)
            },
            {
              title: "What This Means:",
              desc: "If disaster strikes, your insurance might only cover a fraction of what it costs to rebuild. Most homeowners are underinsured by $100,000-$300,000 without realizing it."
            }
          ]
        };
      case 'overpaying':
        return {
          theme: 'success',
          icon: 'FiDollarSign',
          color: 'text-[#10B981]',
          bg: 'bg-green-50',
          border: 'border-green-200',
          headline: "You're Likely Overpaying for Home Insurance",
          intro: "Good news: your home appears to be adequately covered. But you're probably paying more than you need to.",
          sections: [
            {
              title: "💰 Savings Opportunities We See:",
              items: [
                `You haven't shopped rates in a while`,
                "Average California homeowner saves $847/year by comparing quotes",
                "You may qualify for discounts you're not getting (bundling, security systems, claims-free)"
              ]
            },
            {
              title: "The Hidden Cost:",
              desc: "Insurance companies quietly raise rates 3-8% per year. After 3 years without shopping, you could be overpaying by $2,000+."
            }
          ]
        };
      case 'coverage_gaps':
      default:
        return {
          theme: 'warning',
          icon: 'FiHelpCircle',
          color: 'text-[#F97316]',
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          headline: "Let's Make Your Home Insurance Crystal Clear",
          intro: "Your answers show you're not alone - most homeowners don't fully understand what their policy covers (or doesn't cover).",
          sections: [
            {
              title: "🤔 Common Confusion We See:",
              items: [
                "Replacement cost vs. market value (they're very different!)",
                answers.additionalStructures?.length > 1 ? "Whether your additional structures are actually covered" : null,
                "Natural disaster coverage (spoiler: it's NOT in standard policies)",
                "What would actually happen if you had a claim"
              ].filter(Boolean)
            },
            {
              title: "Why This Matters:",
              desc: "When you don't understand your coverage, you can't know if you're protected. And insurance companies don't go out of their way to explain the gaps."
            }
          ]
        };
    }
  };

  const content = getResultContent();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Logo Section */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/525d05658fdf988acaa34e738d3f286826191483600217441074af5254e1324b9947d16ec384fcc03e2360d22f6a7d9b/images/aW1hZ2UvcG5n/312034cd79c028aad1e85c1e994b94aa.png" 
            alt="Pinoy General Insurance Logo" 
            className="h-24 md:h-32 object-contain"
          />
        </div>

        {/* Result Header Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-white rounded-2xl shadow-xl overflow-hidden border-t-8 mb-12 ${
            resultType === 'underinsured' ? 'border-[#EF4444]' : 
            resultType === 'overpaying' ? 'border-[#10B981]' : 'border-[#F97316]'
          }`}
        >
          <div className={`${content.bg} p-8 md:p-12 text-center border-b ${content.border}`}>
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-sm mb-6 ${content.color}`}>
              <SafeIcon icon={content.icon} className="text-4xl" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              {content.headline}
            </h1>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto font-medium">
              {content.intro}
            </p>
          </div>

          <div className="p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Findings */}
              <div className="space-y-8">
                {content.sections.map((section, idx) => (
                  <div key={idx}>
                    <h3 className="text-xl font-bold text-slate-800 mb-4">{section.title}</h3>
                    {section.items ? (
                      <ul className="space-y-3">
                        {section.items.map((item, i) => (
                          <li key={i} className="flex gap-3 text-slate-700">
                            <SafeIcon icon="FiChevronRight" className={`mt-1 flex-shrink-0 ${content.color}`} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-700 leading-relaxed">{section.desc}</p>
                    )}
                  </div>
                ))}
                
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                  <h4 className="font-semibold text-[#1E40AF] mb-2">What to bring to your call:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Current home insurance policy (or dec page)</li>
                    <li>• Recent home improvements/renovations</li>
                    <li>• List of high-value items</li>
                  </ul>
                </div>
              </div>

              {/* TidyCal Embed Area */}
              <div className="bg-slate-50 rounded-2xl p-4 md:p-6 border border-slate-200">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#1E40AF] mb-2">Book Your 15-Min Review</h3>
                  <p className="text-slate-600">Choose a time that works best for you below.</p>
                </div>
                
                {/* TidyCal Embed */}
                <div 
                  className="tidycal-embed" 
                  data-path="pinoygeneralinsurance/discovery-call-phone-or-video"
                  style={{ minHeight: '500px' }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Email Confirmation Footer */}
          <div className="bg-slate-800 text-slate-300 p-6 text-center text-sm">
            <p className="mb-2">
              <SafeIcon icon="FiMail" className="inline mr-2" />
              We just sent a copy of your results to <strong>{formData.email}</strong>
            </p>
            <p>Check your email for your free California Homeowner's Guide.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}